import React from 'react';
import { useSeo } from '../lib/seo';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  BookOpen, Camera, CalendarPlus, Sparkles, RefreshCw, Ticket, LifeBuoy, Mail, ArrowRight, ClipboardList,
} from 'lucide-react';

const SECTIONS = [
  { id: 'listing', icon: Camera, label: 'Your listing' },
  { id: 'new-listing', icon: ClipboardList, label: 'No listing yet?' },
  { id: 'events', icon: CalendarPlus, label: 'Submitting events' },
  { id: 'membership', icon: Sparkles, label: 'Your membership' },
  { id: 'freshness', icon: RefreshCw, label: 'Keeping it fresh' },
  { id: 'tickets', icon: Ticket, label: 'Awards & event tickets' },
  { id: 'resources', icon: LifeBuoy, label: 'Resource centre' },
  { id: 'help', icon: Mail, label: 'Need a hand?' },
];

function GuideSection({ id, icon: Icon, title, children, cta, to }) {
  return (
    <section id={id} className="bg-white border border-charcoal/10 rounded-2xl p-6 md:p-8" data-testid={`guide-section-${id}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-xl bg-heritage/10 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-heritage" />
        </span>
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      <div className="text-charcoal/75 leading-relaxed space-y-3 text-[15px]">{children}</div>
      {cta && to && (
        <Link to={to} className="inline-flex items-center gap-1.5 mt-5 bg-heritage text-cream font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-heritage/90 transition-colors" data-testid={`guide-cta-${id}`}>
          {cta} <ArrowRight size={14} />
        </Link>
      )}
    </section>
  );
}

export default function PartnerGuidePage() {
  useSeo('Partner Guide | Trade | goLeicestershire');
  return (
    <div className="bg-base min-h-screen" data-testid="partner-guide-page">
      <Helmet>
        <title>Getting started — Trade Partner guide · GoLeicestershire</title>
      </Helmet>

      <section className="bg-heritage text-base py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-3 inline-flex items-center gap-2"><BookOpen size={14} /> Trade Partner guide</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-4">Getting started — everything in one place.</h1>
          <p className="text-base/85 text-lg leading-relaxed max-w-2xl">
            Welcome aboard. This page walks you through everything your partner account can do —
            and it&apos;s always here whenever you need it, so bookmark it and come back any time.
          </p>
          <div className="flex flex-wrap gap-2 mt-7">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="inline-flex items-center gap-1.5 border border-base/30 text-base text-xs font-semibold rounded-full px-3.5 py-1.5 hover:bg-base/10 transition-colors" data-testid={`guide-nav-${s.id}`}>
                <s.icon size={12} /> {s.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[1000px] mx-auto px-6 lg:px-12 py-12 md:py-16 space-y-6">
        <section className="bg-forest text-cream rounded-2xl p-6 md:p-8" data-testid="guide-quick-start">
          <h2 className="font-display text-2xl mb-4">Your first five minutes</h2>
          <ol className="space-y-2.5 text-[15px] leading-relaxed list-none">
            {[
              ['1', 'Open My Listing — use the button just below. That page is your editing home.'],
              ['2', 'Check your details — description, opening days, prices, contact info. Fix anything that\u2019s out of date.'],
              ['3', 'Look at your photos — a strong main photo is the single biggest thing you can do for clicks. Upload straight from your device.'],
              ['4', 'Hit Save — your changes go to our editors for a quick check, then they\u2019re live. You\u2019ll see them listed as "pending" until then.'],
              ['5', 'Got an event coming up? Submit it from the same page and we\u2019ll add it to What\u2019s On.'],
            ].map(([n, text]) => (
              <li key={n} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-mustard text-charcoal text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
                <span>{text}</span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/partners/my-listing" data-testid="quick-start-my-listing"
              className="inline-flex items-center gap-1.5 bg-mustard text-charcoal font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-cream transition-colors">
              Open My Listing <ArrowRight size={14} />
            </Link>
            <Link to="/partners/my-listing#submit-event" data-testid="quick-start-submit-event"
              className="inline-flex items-center gap-1.5 border border-cream/40 text-cream font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-cream/10 transition-colors">
              Submit an event <ArrowRight size={14} />
            </Link>
          </div>
        </section>

        <GuideSection id="listing" icon={Camera} title="Making changes to your listing" cta="Open My Listing" to="/partners/my-listing">
          <p>
            If we&apos;ve assigned a listing to your account, <strong>My Listing</strong> is your editing home —
            you&apos;ll find it via the &ldquo;Manage your listing&rdquo; button at the top of the Trade Partners
            page. You can update your description, opening days, prices, contact details, accessibility
            information, and swap or add photos.
          </p>
          <p>
            <strong>How it works:</strong> nothing you save goes live instantly. Every change lands in our
            moderation queue and one of our editors reviews it — usually within a couple of working days. You&apos;ll
            see your pending edits listed at the top of the page until they&apos;re approved, so you always know
            where things stand.
          </p>
          <p>
            <strong>Photos:</strong> upload straight from your device — no need to host them anywhere. If you&apos;re
            uploading a photo, we&apos;ll ask you to confirm you have the rights to use it. Good photos make a
            genuine difference to how many visitors click through, so use your best.
          </p>
        </GuideSection>

        <GuideSection id="new-listing" icon={ClipboardList} title="No listing yet? Request one" cta="Request a listing" to="/partners/my-listing">
          <p>
            If your account isn&apos;t linked to a venue yet, the My Listing page lets you <strong>request a new
            listing</strong> — tell us about your business and our team will build the page and link it to your
            account. Once it&apos;s live, you manage it exactly as described above.
          </p>
        </GuideSection>

        <GuideSection id="events" icon={CalendarPlus} title="Submitting events to What's On" cta="Submit an event" to="/partners/my-listing#submit-event">
          <p>
            Running something visitors would love — a show, a market, a festival, a special opening? Submit it
            for the <strong>What&apos;s On</strong> calendar from your My Listing page. Give us the dates, a short
            description, ticket link and (ideally) a photo.
          </p>
          <p>
            As with listing edits, our editors give every event a quick once-over before it&apos;s published —
            once approved, it appears on the What&apos;s On calendar for visitors to find. If you include a photo,
            you&apos;ll be asked to confirm you hold the rights to use it — that keeps everyone on the right side
            of copyright.
          </p>
        </GuideSection>

        <GuideSection id="membership" icon={Sparkles} title="Your membership — purchase, renew & upgrade" cta="See what each tier includes" to="/partners#tiers">
          <p>Your membership tier decides how much of your story the public site shows:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Gold</strong> — unlimited description, 10 photos, full contact details, best placement everywhere, and a spot in the rotating homepage showcase.</li>
            <li><strong>Silver</strong> — up to 320 words, 5 photos, full contact details, priority placement.</li>
            <li><strong>Bronze</strong> — up to 60 words, 2 photos, website and phone displayed.</li>
            <li><strong>Free entry (no package)</strong> — a basic listing: one photograph, your web link and up to 25 words. Phone and email are not published, and other services are chargeable at our published rates.</li>
          </ul>
          <p>
            You can <strong>purchase, renew or upgrade</strong> your partnership package securely by card, right
            from the <strong>My Listing</strong> page — look for the membership panel. If you try to add more
            photos or words than your tier allows, nothing is lost — we&apos;ll simply invite you to upgrade.
          </p>
        </GuideSection>

        <GuideSection id="freshness" icon={RefreshCw} title="Keeping your listing fresh">
          <p>
            Every three months we&apos;ll send you a friendly nudge to check your listing is still accurate —
            opening hours, prices, photos, the lot. Listings that stay up to date perform noticeably better with
            visitors, and it takes two minutes. If nothing&apos;s changed, you can simply confirm and carry on.
          </p>
        </GuideSection>

        <GuideSection id="tickets" icon={Ticket} title="Awards, forum & event tickets" cta="Book tickets" to="/awards/tickets">
          <p>
            As a trade partner you can book tickets for the <strong>Leicestershire Tourism &amp; Hospitality
            Awards</strong>, the tourism forum and our other industry events. The awards celebrate the best of
            the county&apos;s tourism scene — and winning (or even being shortlisted) puts a trophy badge on your
            public listing.
          </p>
        </GuideSection>

        <GuideSection id="resources" icon={LifeBuoy} title="The resource centre" cta="Browse the resource centre" to="/partners/advice-guidance">
          <p>
            The <strong>advice &amp; guidance hub</strong> is where all the information and guidance reports live:
            accessibility toolkits, regenerative tourism plans, visitor data and insights, marketing toolkits,
            and funding &amp; grants information. We work with VisitEngland, VisitBritain and the regional DMOs
            every week, so where the national bodies have the definitive resource, we link you straight to it.
          </p>
        </GuideSection>

        <GuideSection id="help" icon={Mail} title="Need a hand?">
          <p>
            Stuck on anything, or not sure where something lives? Email us first and we&apos;ll point you straight
            to the answer:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Partnerships — Janine Williams: <a href="mailto:janine.williams@l-p-l.com" className="text-heritage underline">janine.williams@l-p-l.com</a></li>
            <li>General enquiries: <a href="mailto:info@goleicestershire.com" className="text-heritage underline">info@goleicestershire.com</a></li>
          </ul>
        </GuideSection>
      </div>
    </div>
  );
}
